<template>
  <!-- Loading -->
  <mc-loading-indicator
    v-if="showSpinner"
    class="card card--loading"
    variant="spinner"
    appearance="primary"
    fit="large"
    :label="$t('common.loading')"
  />

  <div v-else class="unconfirmed-bookings">
    <div class="mb-4">{{ $t("unconfirmed.description", { brand: brand }) }}</div>

    <!-- No results -->
    <MarketingNotification
      v-if="
        unconfirmedBookingsData && unconfirmedBookingsData.length === 0 && !unconfirmedBookingsError && !showSpinner
      "
      class="card"
    >
      <p
        tabindex="0"
        role="button"
        @keydown.enter="marketingClickThrough"
        @click="marketingClickThrough"
        v-html="$t('shipments.oceanUnconfirmedNotification')"
      />

      <mc-button class="mt-2 mb-4" :label="$t('shipments.oceanButton')" @click="navigateToOceanBooking" />
    </MarketingNotification>

    <!-- Error -->
    <mc-notification
      v-else-if="unconfirmedBookingsData && unconfirmedBookingsData.length === 0 && !showSpinner"
      class="card"
      width="fit-content"
      appearance="error"
      icon="exclamation-circle"
      :body="$t('datatable.error.noRecords500')"
    />

    <!-- Results -->
    <mc-table
      v-else
      fit="small"
      sortdisabled
      :data.prop="unconfirmedBookingsData"
      :columns.prop="[
        {
          id: 'blNumber',
          label: $t('common.blNumber'),
          subDataLabel: $t('tables.references'),
          verticalAlign: 'top',
          width: '175px'
        },
        {
          id: 'from',
          label: $t('common.from'),
          subDataLabel: $t('tables.depPort'),
          verticalAlign: 'top'
        },
        {
          id: 'to',
          label: $t('common.to'),
          subDataLabel: $t('tables.arrPort'),
          verticalAlign: 'top'
        },
        {
          id: 'vessel',
          label: $t('common.vessel'),
          verticalAlign: 'top'
        },
        {
          id: 'cargo',
          label: $t('common.cargo'),
          verticalAlign: 'top',
          width: '175px'
        }
      ]"
      data-test="unconfirmed-bookings-table"
    >
      <template v-for="item in unconfirmedBookingsData" :key="item.id">
        <div :slot="`${item.id}_blNumber`">
          <a
            data-test="document-table-shipment-link"
            :href="`/shipment-details/${item.ladingNumber}`"
            @click="trackShipmentClick('unconfirmedbookings', $route?.path?.toLowerCase()?.includes?.('export'))"
          >
            <b>{{ item.ladingNumber }}</b>
          </a>
          <p v-if="item.bookedByRef" class="color-neutral-subtle mds-text--small-normal wrap-anywhere">
            {{ $t("common.bookedByRef") }},
            {{ item.bookedByRef }}
          </p>
        </div>
        <div :slot="`${item.id}_from`">
          <FromTo :place="item?.placeOfReceipt || item?.portOfLoading" :port="item?.portOfLoading" />
        </div>
        <div :slot="`${item.id}_to`">
          <FromTo
            :place="item?.placeOfDelivery || item?.portOfDischarge"
            :port="item?.portOfDischarge"
            type="arrival"
          />
        </div>
        <div :slot="`${item.id}_vessel`">
          <Vessel :vessel="item" />
        </div>
        <div :slot="`${item.id}_cargo`">
          <Cargo
            :commodity="item?.cargo?.commodity"
            :packageQuantity="item?.cargo?.containerQuantity"
            :packageQuantityUnit="item?.cargo?.containerName"
          />
        </div>
      </template>
    </mc-table>

    <Pagination
      v-if="unconfirmedBookingsData && unconfirmedBookingsData.length > 0 && lastPage > 1"
      activeView="Unconfirmed_bookings"
      :lastPage="lastPage"
      :totalCount="totalCount"
      :currentPage="paginationToken"
      @update-data-source="getData"
    />
  </div>
</template>
<script>
import { getBrandFromHostname } from "@maersk-global/shared-js";
import Pagination from "@/components/shared/Pagination.vue";
import MarketingNotification from "@/components/shared/MarketingNotification.vue";
import { mapActions, mapState } from "pinia";
import { filters } from "@/store/filters";
import { unconfirmedBookings } from "@/store/unconfirmedBookings";
import FromTo from "@/components/shared/FromTo.vue";
import Cargo from "@/components/shared/Cargo.vue";
import Vessel from "@/components/shared/Vessel.vue";
import "@maersk-global/mds-components-core/mc-table";
import { trackShipmentClick } from "@/lib/utilities";

export default {
  name: "UnconfirmedBookings",
  components: { Pagination, MarketingNotification, FromTo, Cargo, Vessel },
  computed: {
    ...mapState(unconfirmedBookings, [
      "unconfirmedBookingsData",
      "unconfirmedBookingsError",
      "lastPage",
      "totalCount",
      "showSpinner",
      "unconfirmedBookingsError",
      "paginationToken"
    ]),
    ...mapState(filters, ["activeFilter"]),
    brand() {
      return getBrandFromHostname(window.location)?.commercialName;
    }
  },
  watch: {
    activeFilter: {
      async handler() {
        await this.updatePaginationToken(1);
        await this.getUnconfirmedBookings();
      },
      deep: true
    }
  },
  async mounted() {
    if (this.unconfirmedBookingsData.length === 0) {
      await this.getUnconfirmedBookings();
    }
  },

  methods: {
    ...mapActions(unconfirmedBookings, ["getUnconfirmedBookings", "updatePaginationToken"]),
    trackShipmentClick,
    getData(event) {
      this.updatePaginationToken(event);
    },
    async navigateToOceanBooking() {
      this.$sendGAEvent({
        event: "DQCS",
        category: "Component",
        action: "Click",
        label: { data: "SoOceanBookingClick" }
      });
      window.location.href = `/book`;
    },
    marketingClickThrough() {
      this.$sendGAEvent({
        category: "Component",
        event: "DQCS",
        action: "Click",
        label: {
          data: "SoOceanLinkClick"
        }
      });
    }
  }
};
</script>
